import {
  useCallback, useEffect, useMemo, useRef, memo, forwardRef,
} from 'react';
import MaterialTable, {
  Components as MaterialTableComponents,
  EditCellColumnDef,
  MaterialTableProps,
  MTableBody,
  MTableBodyRow,
  MTableHeader,
  Options as MaterialTableOptions,
} from '@material-table/core';
import { ScrollSync, type ScrollSyncChildProps } from 'react-virtualized';
import { useOverlayScrollbars, type UseOverlayScrollbarsParams } from 'overlayscrollbars-react';
import { type TFunction } from 'i18next';
import { merge, keyBy } from 'lodash';
import {
  setCustomScrollbarInitializationAttribute,
  DEFAULT_SCROLLBAR_PARAMS,
  IComponentWithCustomScrollbarProps,
} from '../../services';
import { mergeSX } from '../../services/helpers';
import {
  AB1, S5, S2, S4, P4, AR4, AB3, S3,
} from '../../theme';
import { ArrowIcon } from '../../icons';
import Paper from '../Paper';
import CustomPagination from '../Pagination';
import Box from '../Box';
import TableBody from './components/TableBody';
import useTableDimensions from '../../hooks/useTableDimensions';
import useVirtualizedTableColumnWidths from '../../hooks/useVirtualizedTableColumnWidths';
import { IUseTableReturn, usePrevious } from '../../hooks';
import { TYPOGRAPHY_SUBTITLE1 } from '../Typography';
import UnselectedCheckbox from '../../icons/UnselectedCheckbox';
import SelectedCheckbox from '../../icons/SelectedCheckbox';
import IndeterminateCheckbox from '../../icons/IndeterminateCheckbox';
import { CHECKBOX_ICONS_SIZE } from '../Checkbox/DefaultCheckbox';
import { CheckboxSizeEnum } from '../../enums';

export interface IDefaultRowData {
  tableData: EditCellColumnDef['tableData'];
  [key: string]: any;
}

export interface IMaterialTableProps<RowData extends object> extends Omit<
  MaterialTableProps<RowData>, 'data' | 'onSelectionChange'
>, Pick<
  IUseTableReturn<RowData>, 'activePage' | 'perPage' | 'onPerPageChange'
>, IComponentWithCustomScrollbarProps {
  t?: TFunction<'translation', undefined>;
  /**
   * stretchTableVertically - if set to false, will remove min-height thus there will be no blank
   * space if there are only a few rows. Defaults to true.
   * */
  stretchTableVertically?: boolean;
  cellPadding?: string | number;
  hidePagination?: boolean;
  keepSortDirectionOnColumnSwitch?: boolean;
  totalItems: number;
  rowSelectByKey?: string;
  rowHeight?: number;
  data: readonly RowData[];
  withRowValidity?: boolean;
  rowValidityKey?: string;
  disableVirtualization?: boolean;
  onActivePageChange: IUseTableReturn<RowData>['setActivePage'];
  selectedRows?: IUseTableReturn<RowData>['selectedRows'];
  onSelectionChange?: IUseTableReturn<RowData>['onRowSelectionChange'];
  columnOrderBy?: IUseTableReturn<RowData>['columnOrderBy'];
  onColumnOrderByChange?: IUseTableReturn<RowData>['onColumnOrderByChange'];
  columnOrderDirection?: IUseTableReturn<RowData>['columnOrderDirection'];
  onColumnOrderDirectionChange?: IUseTableReturn<RowData>['onColumnOrderDirectionChange'];
  bottomMargin?: number;
  hasBottomShadow?: boolean;
}

export interface IMaterialTableOptions<
  RowData extends object
> extends MaterialTableOptions<RowData> {
}

interface IBuildContainerSXParams<RowData extends object> {
  hasNoData: boolean;
  cellPadding?: IMaterialTableProps<RowData>['cellPadding'];
  rowHeight?: IMaterialTableProps<RowData>['rowHeight'];
  disableVirtualization: IMaterialTableProps<RowData>['disableVirtualization'];
  selection?: IMaterialTableOptions<RowData>['selection'];
  cursorDefault: boolean;
  hasBottomShadow?: boolean;
}

interface IBuildTableBodyRowSXParams {
  isSelected: boolean;
  isDisabled: boolean;
  isInvalid: boolean;
}

const buildTableBodyRowSX = ({ isSelected, isDisabled, isInvalid }: IBuildTableBodyRowSXParams) => {
  // todo add isLoading + custom spinner
  const isSelectedSX = isSelected ? {
    backgroundColor: S2,
  } : {};

  const isDisabledSX = isDisabled ? {
    color: S4,
  } : {};

  const isInvalidSX = isInvalid ? {
    backgroundColor: `${AR4} !important`,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: `${AB1} !important`,
    },
  } : {};

  return {
    ...isSelectedSX,
    ...isDisabledSX,
    ...isInvalidSX,
  };
};

const buildArrowIconSX = () => ({
  '&.MuiTableSortLabel-icon': {
    fontSize: 10,
    marginRight: 0,
    marginLeft: '5px',
    color: 'inherit',
  },
});

// while changing this value, also change 'selectionCellStyles' in 'buildContainerSX'
const SELECTION_CELL_WIDTH = 48;

const buildContainerSX = <RowData extends object>({
  hasNoData,
  rowHeight,
  cellPadding,
  disableVirtualization,
  selection,
  cursorDefault,
  hasBottomShadow,
}: IBuildContainerSXParams<RowData>) => {
  const tableBodyNoDataStyles = hasNoData ? {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    backgroundColor: S2,
    marginTop: '50px',
  } : {};

  const tableRowNoDataStyles = hasNoData ? {
    height: 'auto !important',
  } : {};

  const tableRowHoverNoDataStyles = hasNoData ? {
    backgroundColor: S2,
    cursor: 'default',
  } : {};

  const tableRowHoverNotVirtualizedTableStyles = disableVirtualization ? {
    '&:nth-of-type(odd)': {
      backgroundColor: S2,
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: AB1,
        ...tableRowHoverNoDataStyles,
      },
    },
  } : {};

  const tableCellNoDataStyles = hasNoData ? {
    fontSize: 16,
  } : {};

  const selectionCellVirtualizedTableStyles = !disableVirtualization ? {
    /*
     on the virtualized table (that always has "table-layout: 'auto'"), setting the width to 0% will
     force the selection cell to take the correct amount of space and not expand on browser resizing
    */
    width: '0% !important',
  } : {};

  const selectionCellStyles = selection ? {
    '&:first-of-type': {
      ...selectionCellVirtualizedTableStyles,
      padding: 0,
      '& > span': {
        ml: '6px !important', // combines with SELECTION_CELL_WIDTH
      },
    },
  } : {};

  const tableCellPadding = cellPadding ?? (!disableVirtualization ? '0 16px' : undefined);

  return {
    filter: hasBottomShadow ? 'drop-shadow(0px 4px 4px rgba(51, 51, 51, 0.25))' : 'none',
    '&.MuiPaper-root': {
      color: S5,
    },
    '& .MuiTableCell-head.MuiTableCell-root': {
      height: 54,
      py: 0,
      color: S5,
      ...TYPOGRAPHY_SUBTITLE1,
    },
    '& .MuiTableSortLabel-root': {
      '&.Mui-active': {
        color: P4,
        '& .MuiTableSortLabel-icon': {
          color: 'inherit',
        },
      },
    },
    '& .MuiTableCell-root': {
      border: 'none',
      wordBreak: 'break-word',
      lineHeight: '19px',
      cursor: cursorDefault ? 'default' : undefined,
      padding: tableCellPadding,
      ...tableCellNoDataStyles,
      ...selectionCellStyles,
    },
    '& .MuiTableBody-root': {
      ...tableBodyNoDataStyles,
      '& .MuiTableRow-root': {
        fontSize: 14,
        height: rowHeight,
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: AB1,
        },
        ...tableRowNoDataStyles,
        ...tableRowHoverNotVirtualizedTableStyles,
      },
    },
    '& .MuiCheckbox-root': {
      color: S4,
      '&.Mui-disabled': {
        color: S3,
      },
      '&.Mui-checked': {
        color: AB3,
        '&.Mui-disabled': {
          color: S3,
        },
      },
      '&.MuiCheckbox-indeterminate': {
        color: AB3,
      },
    },
  };
};

// TODO OStarchak add correct types
const SortArrow = (props: any) => (
  <Box component="span" sx={buildArrowIconSX()} {...props}>
    <ArrowIcon />
  </Box>
);

const SortArrowRef = forwardRef<unknown>((sortArrowProps, iconRef) => (
  <SortArrow
    ref={iconRef}
    {...sortArrowProps}
  />
));

const TABLE_ICONS = {
  SortArrow: SortArrowRef,
};

const Table = <RowData extends object>(props: IMaterialTableProps<RowData>) => {
  const {
    t,
    stretchTableVertically = true,
    data,
    columns: preColumns,
    perPage,
    onPerPageChange,
    activePage,
    onActivePageChange,
    options: preOptions,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    totalItems,
    rowSelectByKey,
    selectedRows,
    onSelectionChange,
    isLoading,
    keepSortDirectionOnColumnSwitch = false,
    rowHeight,
    cellPadding,
    hidePagination = false,
    withRowValidity = false,
    rowValidityKey,
    bottomMargin,
    disableVirtualization: preDisableVirtualization = false,
    tableRef: propsTableRef,
    disableCustomScrollbar = false,
    onRowClick,
    hasBottomShadow = true,
    ...restTableProps
  } = props;

  if (process.env.NODE_ENV !== 'production' && !preDisableVirtualization) {
    if (typeof rowHeight !== 'number') {
      throw new Error(
        'Virtualized table requires static row height. Either provide the \'rowHeight\' property '
        + 'or disable virtualization via the \'disableVirtualization\' property.',
      );
    }
    if (preOptions?.tableLayout === 'fixed') {
      // eslint-disable-next-line no-console
      console.warn(
        'The virtualized table uses "tableLayout: \'auto\'", which is adapted and should'
          + ' support all of the use cases. Either omit \'tableLayout\', set \'tableLayout\''
          + ' to \'auto\', or consider disabling virtualization via the'
          + ' \'disableVirtualization\' property.',
      );
    }
  }

  const hasNoData = useMemo(() => !isLoading && !totalItems, [isLoading, totalItems]);

  const disableVirtualization = hasNoData || preDisableVirtualization;

  const optionalRef = useRef(null);

  const tableRef = propsTableRef ?? optionalRef;

  const count = Math.ceil(totalItems / perPage);

  const scrollSyncOnScrollHandler = useRef<ScrollSyncChildProps['onScroll']>();
  const [initialize, instance] = useOverlayScrollbars(merge<
    unknown,
    UseOverlayScrollbarsParams,
    UseOverlayScrollbarsParams
  >(
    {},
    DEFAULT_SCROLLBAR_PARAMS,
    {
      events: {
        scroll: (_, event) => {
          if (!scrollSyncOnScrollHandler.current) {
            /*
              a table with disabled virtualization may have a custom scroll, but there is no need
              to synchronize it
            */
            return;
          }
          const {
            clientHeight, clientWidth, scrollHeight, scrollLeft, scrollTop, scrollWidth,
          } = event.target as HTMLElement;
          scrollSyncOnScrollHandler.current({
            clientHeight, clientWidth, scrollHeight, scrollLeft, scrollTop, scrollWidth,
          });
        },
      },
    },
  ));

  useEffect(() => {
    if (!tableRef.current) {
      return;
    }

    /*
      Force table to store the actual 'activePage' and 'perPage' values.
      To see why this code is needed - comment these lines below, go to the 'PerPageIssue' story,
      and try adding a new user -> 'Number of users' will increase, but added user(s) won't be shown
      in the table.
    */

    tableRef.current.onPageChange(null, activePage);
    tableRef.current.onRowsPerPageChange({ target: { value: perPage } });
  }, [tableRef, activePage, perPage]);

  useEffect(() => {
    if (disableCustomScrollbar) {
      return;
    }
    const currInstance = instance();
    if (!data.length) { // destroy the scroll instance when there is no data
      currInstance?.destroy();
      return;
    }
    if (!currInstance || currInstance.state().destroyed) {
      // if data is present, but there is no current instance, or it was destroyed - initialize it
      const target = tableRef.current?.tableContainerDiv.current?.offsetParent;
      setCustomScrollbarInitializationAttribute(target);
      initialize(target);
      return;
    }
    // otherwise - update the instance so the scroll position is preserved across data changes
    currInstance.update();
  }, [
    disableCustomScrollbar,
    instance,
    data,
    tableRef,
    initialize,
  ]);

  useEffect(() => {
    const currInstance = instance();
    if (!currInstance || currInstance.state().destroyed) {
      return;
    }
    const { viewport } = currInstance.elements();
    // scroll to the top of the viewport on each 'activePage' change
    viewport.scrollTo({ top: 0 });
  }, [instance, activePage]);

  const { tableWidth, tableHeight, paginationRef } = useTableDimensions<RowData>(
    {
      tableRef,
      skipPagination: !totalItems || hidePagination,
      bottomMargin,
      data,
    },
  );

  const previousColumnOrderBy = usePrevious(columnOrderBy);
  const previousColumnOrderDirection = usePrevious(columnOrderDirection);

  useEffect(() => {
    if (!tableRef.current) {
      return;
    }
    const isColumnOrderByCleared = !!previousColumnOrderBy && !columnOrderBy;
    const isColumnOrderDirectionCleared = !!previousColumnOrderDirection && !columnOrderDirection;
    if (!isColumnOrderByCleared && !isColumnOrderDirectionCleared) {
      return;
    }
    tableRef.current.onChangeOrder();
  }, [
    tableRef,
    previousColumnOrderBy, columnOrderBy,
    previousColumnOrderDirection, columnOrderDirection,
  ]);

  const options = useMemo<IMaterialTableOptions<RowData>>(() => ({
    ...preOptions,
    selection: data.length ? preOptions?.selection : false,
    actionsColumnIndex: -1,
    toolbar: false,
    pageSize: hasNoData ? 0 : perPage,
    headerStyle: { position: 'sticky', zIndex: 2 },
    maxBodyHeight: tableHeight,
    minBodyHeight: stretchTableVertically ? tableHeight : 0,
    showTextRowsSelected: false,
    emptyRowsWhenPaging: false,
    overflowY: 'initial',
    keepSortDirectionOnColumnSwitch,
    idSynonym: rowSelectByKey,
    // virtualized table's columns and header better works with "tableLayout: 'auto'"
    tableLayout: !disableVirtualization ? 'auto' : preOptions?.tableLayout,
  }), [
    preOptions,
    perPage,
    hasNoData,
    data,
    keepSortDirectionOnColumnSwitch,
    tableHeight,
    stretchTableVertically,
    disableVirtualization,
    rowSelectByKey,
  ]);

  const { selection } = options;

  const columns = useVirtualizedTableColumnWidths<RowData>({
    columns: preColumns,
    tableWidth,
    disableVirtualization,
    selection,
    selectionColumnWidth: SELECTION_CELL_WIDTH,
  });

  const Row = useCallback<
    NonNullable<MaterialTableComponents['Row']>
  >((rowProps) => {
    const { sx, options: rowOptions, ...restRowProps } = rowProps;
    const isSelected = rowProps.data.tableData.checked;
    const isDisabled = rowProps.data.tableData.isRowDisabled;
    const isInvalid = !!(withRowValidity && rowValidityKey && !rowProps.data[rowValidityKey]);
    return (
      <MTableBodyRow
        {...restRowProps}
        // original sx should be first - merge custom sx into original sx
        sx={mergeSX(sx, buildTableBodyRowSX({ isSelected, isDisabled, isInvalid }))}
        options={{
          ...rowOptions,
          selectionProps: {
            ...rowOptions.selectionProps,
            icon: <UnselectedCheckbox {...CHECKBOX_ICONS_SIZE[CheckboxSizeEnum.Medium]} />,
            checkedIcon: <SelectedCheckbox {...CHECKBOX_ICONS_SIZE[CheckboxSizeEnum.Medium]} />,
          },
        }}
      />
    );
  }, [withRowValidity, rowValidityKey]);

  const Header = useCallback<
    NonNullable<MaterialTableComponents['Header']>
  >((headerProps) => {
    const { options: headerOptions, ...restHeaderProps } = headerProps;
    return (
      <MTableHeader
        {...restHeaderProps}
        options={{
          ...headerOptions,
          headerSelectionProps: {
            ...headerOptions.headerSelectionProps,
            indeterminateIcon: <IndeterminateCheckbox
              {...CHECKBOX_ICONS_SIZE[CheckboxSizeEnum.Medium]}
            />,
            icon: <UnselectedCheckbox {...CHECKBOX_ICONS_SIZE[CheckboxSizeEnum.Medium]} />,
            checkedIcon: <SelectedCheckbox {...CHECKBOX_ICONS_SIZE[CheckboxSizeEnum.Medium]} />,
          },
        }}
      />
    );
  }, []);

  const hasOnRowClick = !!onRowClick;
  const Container = useCallback<
    NonNullable<MaterialTableComponents['Container']>
  >(({ className, ...rest }) => (
    <Paper
      elevation={0}
      className={className}
      sx={buildContainerSX({
        hasNoData,
        rowHeight,
        cellPadding,
        disableVirtualization,
        selection,
        cursorDefault: !hasOnRowClick,
        hasBottomShadow,
      })}
      {...rest}
    />
  ), [
    hasNoData,
    rowHeight,
    cellPadding,
    disableVirtualization,
    selection,
    hasOnRowClick,
    hasBottomShadow,
  ]);

  const Pagination = useCallback<
    NonNullable<MaterialTableComponents['Pagination']>
  >(
    (params) => {
      if (hidePagination) {
        return null;
      }
      const { onRowsPerPageChange } = params;
      return (
        <CustomPagination
          t={t}
          ref={paginationRef}
          perPage={perPage}
          onPerPageChange={(e) => {
            onRowsPerPageChange(e);
            onPerPageChange(e.target.value as number);
          }}
          activePage={activePage}
          onActivePageChange={onActivePageChange}
          count={count}
          totalItems={totalItems}
          disableCustomScrollbar={disableCustomScrollbar}
        />
      );
    },
    [
      count,
      totalItems,
      perPage,
      activePage,
      t,
      onPerPageChange,
      onActivePageChange,
      hidePagination,
      disableCustomScrollbar,
      paginationRef,
    ],
  );

  const handleOrderChange = useCallback<
    NonNullable<MaterialTableProps<RowData>['onOrderChange']>
  >((orderBy, orderDirection) => {
    if (!onColumnOrderByChange || !onColumnOrderDirectionChange) {
      return;
    }
    onColumnOrderByChange(columns[orderBy]?.field as string);
    onColumnOrderDirectionChange(orderDirection);
  }, [onColumnOrderByChange, onColumnOrderDirectionChange, columns]);

  const Body = useCallback<
    NonNullable<MaterialTableComponents['Body']>
  >(
    (bodyProps) => {
      if (disableVirtualization) {
        // no need to sync scroll on table without virtualization
        return <MTableBody {...bodyProps} />;
      }

      const virtualizedBodyProps = {
        ...bodyProps,
        rowHeight,
        tableRef,
        selection,
      };

      if (disableCustomScrollbar) {
        return <TableBody {...virtualizedBodyProps} />;
      }

      // only synchronize scrollbar when table uses virtualization
      return (
        <ScrollSync>
          {({ onScroll }) => {
            scrollSyncOnScrollHandler.current = onScroll;
            return (
              <TableBody {...virtualizedBodyProps} />
            );
          }}
        </ScrollSync>
      );
    },
    [disableVirtualization, tableRef, disableCustomScrollbar, rowHeight, selection],
  );

  const components = useMemo(
    () => ({
      Container, Body, Row, Pagination, Header,
    }),
    [Container, Body, Row, Pagination, Header],
  );

  const handleSelectionChange = useCallback<
    NonNullable<MaterialTableProps<RowData>['onSelectionChange']>
  >((rows, row) => {
    if (!onSelectionChange || !rowSelectByKey || !selectedRows) return;
    const newSelection = new Map(selectedRows);
    if (row) {
      // @ts-ignore
      if (row.tableData.checked) {
        newSelection.set(row[rowSelectByKey as keyof RowData] as string, row);
      } else {
        newSelection.delete(row[rowSelectByKey as keyof RowData] as string);
      }
    } else if (rows.length) {
      rows.forEach((el: RowData) => newSelection.set((el as IDefaultRowData)[rowSelectByKey], el));
    } else {
      data.forEach((el) => newSelection.delete((el as IDefaultRowData)[rowSelectByKey]));
    }
    onSelectionChange(newSelection);
  }, [data, onSelectionChange, rowSelectByKey, selectedRows]);

  const tableData = useMemo(() => {
    if (!selectedRows || !rowSelectByKey) return data;
    return data.map((
      el,
    ) => (
      { ...el, tableData: { checked: selectedRows.has((el as IDefaultRowData)[rowSelectByKey]) } }
    ));
  }, [data, selectedRows, rowSelectByKey]);

  const prevData = usePrevious(data);

  useEffect(() => {
    if (!onSelectionChange || !selectedRows || !selectedRows.size || !rowSelectByKey) return;
    if (prevData === undefined) return;
    if (prevData === data) return;
    const dataByKey = keyBy(data, rowSelectByKey);
    const newSelection = new Map();
    selectedRows.forEach((row) => {
      const rowKey = row[rowSelectByKey as keyof RowData] as string;
      // if dataByKey[rowKey] is missing - different data was selected, use current row
      // we use ...row to copy tableData property that doesn't exist in dataByKey[rowKey]
      const newRow = dataByKey[rowKey] ? { ...row, ...dataByKey[rowKey] } : row;
      newSelection.set(rowKey, newRow);
    });
    onSelectionChange(newSelection);
  }, [onSelectionChange, prevData, rowSelectByKey, selectedRows, data]);

  return (
    <MaterialTable
      {...restTableProps}
      onRowClick={onRowClick}
      isLoading={isLoading}
      data={tableData as RowData[]}
      columns={columns}
      tableRef={tableRef}
      options={options}
      onOrderChange={handleOrderChange}
      components={components}
      icons={TABLE_ICONS}
      onSelectionChange={handleSelectionChange}
    />
  );
};

export default memo(Table) as typeof Table;
